import debounce from 'debounce';
import KeenSlider from 'keen-slider';
import 'keen-slider/keen-slider.min.css';
class HeroCarousel extends HTMLElement {
  constructor() {
    super();
    // elements
    this.keenSlider = null;
    this.slider = this.querySelector('.keen-slider');
    this.prevSelector = '[data-action=prev]';
    this.nextSelector = '[data-action=next]';
    this.togglePlaySelector = '[data-action=toggleplay]';
    this.legends = this.querySelectorAll('.c-block-hero__legend');
    this.refreshArrows = null; // Store reference to refreshArrows
    this.handlePageClick = this.handlePageClick.bind(this); // Bind handlePageClick to the class instance
    this.isPlaying = true; // Start with autoplay enabled
  }
  connectedCallback() {
    // bindings
    this.bndHandleResize = debounce(this.handleResize.bind(this), 250);
    // events
    this.onLoad();
    window.addEventListener('resize', this.bndHandleResize);
  }
  disconnectedCallback() {
    window.removeEventListener('resize', this.bndHandleResize);
    if (this.keenSlider.destroy) this.keenSlider.destroy();
  }
  onLoad() {
    this.initKeen();
  }
  handleResize() {
    if (this.refreshArrows) {
      this.refreshArrows();
    }
  }
  handlePageClick(e) {
    const trigger = e.target.closest('[data-action=page]');
    if (trigger) {
      e.preventDefault();
      e.stopPropagation();
      const target = parseInt(trigger.getAttribute('data-index'), 10);
      if (isNaN(target)) return;
      this.keenSlider?.moveToIdx(target);
    }
  }
  initKeen() {
    if (!this.slider) return;
    this.keenSlider = new KeenSlider(this.slider, {
      loop: true,
      mode: 'free-snap',
      slides: {
        perView: 1
      },
      created: () => {
        this.slider.classList.add('is-ready');
      }
    }, [slider => {
      const next = this.querySelector(this.nextSelector);
      const prev = this.querySelector(this.prevSelector);
      const refreshArrows = () => {
        const details = slider.track.details;
        const current = details?.rel ?? 0;
        prev?.removeAttribute('disabled');
        next?.removeAttribute('disabled');
        if (current === 0 && slider.options.loop != true || slider.slides.length == 0) {
          prev?.setAttribute('disabled', 'disabled');
        }
        if (current >= details.maxIdx && slider.options.loop != true || slider.slides.length == 0) {
          next?.setAttribute('disabled', 'disabled');
        }
      };
      // Store reference to refreshArrows function
      this.refreshArrows = refreshArrows;
      slider.on('created', () => {
        refreshArrows();
        slider.container?.classList.add('is-ready');
        next?.addEventListener('click', () => {
          slider.next();
        });
        prev?.addEventListener('click', () => {
          slider.prev();
        });
        document.addEventListener('click', this.handlePageClick);
      });
      slider.on('slideChanged', () => {
        refreshArrows();
        const details = slider.track.details;
        if (this.current) {
          this.current.textContent = details.rel + 1;
        }

        // Update aria-current attribute on page indicators
        const pageButtons = this.querySelectorAll('[data-action=page]');
        pageButtons.forEach((button, index) => {
          if (index === details.rel) {
            button.setAttribute('aria-current', 'true');
          } else {
            button.removeAttribute('aria-current');
          }
        });

        // Update active legend
        this.legends.forEach(legend => {
          legend.classList.remove('is-active');
          if (parseInt(legend.getAttribute('data-slide')) === details.rel) {
            legend.classList.add('is-active');
          }
        });
      });
      slider.on('animationStarted', () => {
        // Reset progress to 0 immediately when slide changes
        this.style.setProperty('--slide-progress', '0');
      });
    },
    // Add autoplay functionality
    slider => {
      let timeout;
      let animationFrame;
      const duration = 10000; // 10 seconds duration
      const togglePlay = this.querySelector(this.togglePlaySelector);
      const clearNextTimeout = () => {
        clearTimeout(timeout);
        if (animationFrame) {
          cancelAnimationFrame(animationFrame);
        }
      };
      const updateProgress = startTime => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(Math.round(elapsed / duration * 100), 100);
        this.style.setProperty('--slide-progress', `${progress}`);
        if (elapsed < duration && this.isPlaying) {
          animationFrame = requestAnimationFrame(() => updateProgress(startTime));
        }
      };
      const nextTimeout = () => {
        clearNextTimeout();
        if (!this.isPlaying) return;
        const startTime = Date.now();
        this.style.setProperty('--slide-progress', '0');
        animationFrame = requestAnimationFrame(() => updateProgress(startTime));
        timeout = setTimeout(() => {
          if (this.isPlaying) {
            slider.next();
          }
        }, duration);
      };
      const playLabel = togglePlay?.getAttribute('data-play') || '';
      const pauseLabel = togglePlay?.getAttribute('data-pause') || '';
      const togglePlayPause = () => {
        this.isPlaying = !this.isPlaying;

        // Update button aria attributes and appearance
        if (togglePlay) {
          if (this.isPlaying) {
            togglePlay.setAttribute('aria-label', pauseLabel);
            togglePlay.setAttribute('aria-pressed', 'false');
            this.classList.add('is-playing'); // Add class to root element
            nextTimeout(); // Resume autoplay
          } else {
            togglePlay.setAttribute('aria-label', playLabel);
            togglePlay.setAttribute('aria-pressed', 'true');
            this.classList.remove('is-playing'); // Remove class from root element
            clearNextTimeout(); // Stop autoplay
          }
        }
      };
      slider.on('created', () => {
        // Initialize the toggle play button
        if (togglePlay) {
          togglePlay.setAttribute('aria-pressed', this.isPlaying ? 'false' : 'true');
          togglePlay.setAttribute('aria-label', this.isPlaying ? pauseLabel : playLabel);
          togglePlay.addEventListener('click', togglePlayPause);

          // Add initial class state
          if (this.isPlaying) {
            this.classList.add('is-playing');
          } else {
            this.classList.remove('is-playing');
          }
        }
        nextTimeout();
      });
      slider.on('dragStarted', clearNextTimeout);
      slider.on('animationEnded', () => {
        if (this.isPlaying) {
          nextTimeout();
        }
      });
      slider.on('updated', () => {
        if (this.isPlaying) {
          nextTimeout();
        }
      });
    }]);
  }
}
customElements.define('hero-carousel', HeroCarousel);